/**
 * Keymap persistence (#64).
 *
 * The serialized keymap is a versioned, Zod-validated JSON shape. Parsing NEVER
 * throws: corrupt / legacy / partial data degrades to `null` (caller falls back
 * to `DEFAULT_KEYMAP` via the resolver). This mirrors the silent-default
 * discipline of `prefs.ts` — keybinds are convenience state, not load-bearing.
 *
 * A `version` field guards forward migration: a stored keymap whose version
 * doesn't match the current `KEYMAP_VERSION` is treated as absent (returns
 * null → defaults). A real migration table can replace that when the shape
 * first changes; until then "unknown version → defaults" is the safe rule and
 * never corrupts play.
 *
 * The stored shape is a FLAT list of `{ action, chords }`, where `action` is a
 * tagged record matching `GameAction`. We re-validate the action against the
 * branded schemas at the boundary (via the protocol constructors) so a
 * hand-edited / drifted blob can't smuggle an out-of-range slot into the live
 * keymap.
 */

import { z } from "zod";
import { toAbilitySlot, MAX_ABILITY_SLOT } from "@pit/protocol";
import type { GameAction } from "./actions.js";
import { MAX_TARGET_INDEX } from "./actions.js";
import type { KeyChord, ModifierSet } from "./chord.js";
import type { Keymap, KeyBinding } from "./keymap.js";
import { KEYMAP_VERSION } from "./keymap.js";

const MODIFIER_SET_SCHEMA = z.object({
  ctrl: z.boolean(),
  alt: z.boolean(),
  shift: z.boolean(),
  meta: z.boolean(),
});

const KEY_CHORD_SCHEMA = z.object({
  key: z.string().min(1).max(32),
  mods: MODIFIER_SET_SCHEMA,
});

/**
 * Serialized action shape. A discriminated union mirroring `GameAction`;
 * `castSlot.slot` is bounded to the valid range at parse time so a bad blob is
 * rejected (→ null → defaults) rather than trusted.
 */
const STORED_ACTION_SCHEMA = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("castSlot"), slot: z.number().int().gte(0).lte(MAX_ABILITY_SLOT) }),
  z.object({ kind: z.literal("targetNearestEnemy") }),
  z.object({ kind: z.literal("cycleEnemyTarget") }),
  z.object({ kind: z.literal("cycleAllyTarget") }),
  z.object({ kind: z.literal("clearHostile") }),
  z.object({ kind: z.literal("clearFriendly") }),
  z.object({ kind: z.literal("arenaTarget"), index: z.number().int().gte(0).lte(MAX_TARGET_INDEX) }),
  z.object({ kind: z.literal("partyTarget"), index: z.number().int().gte(0).lte(MAX_TARGET_INDEX) }),
  z.object({ kind: z.literal("stopCasting") }),
  z.object({ kind: z.literal("toggleAutoAttack") }),
  z.object({ kind: z.literal("openChat") }),
]);

const STORED_BINDING_SCHEMA = z.object({
  action: STORED_ACTION_SCHEMA,
  // min(0): an action can be deliberately unbound (empty chord list) — e.g.
  // `clearAction` — and must still round-trip through persistence rather
  // than failing to parse.
  chords: z.array(KEY_CHORD_SCHEMA).min(0).max(8),
});

/** The full persisted keymap envelope: version + bindings. */
export const STORED_KEYMAP_SCHEMA = z.object({
  version: z.number().int(),
  bindings: z.array(STORED_BINDING_SCHEMA).max(256),
});

export type StoredKeymap = z.infer<typeof STORED_KEYMAP_SCHEMA>;

/** Serialize a live keymap to the stored envelope (current version). */
export function serializeKeymap(keymap: Keymap): StoredKeymap {
  return {
    version: KEYMAP_VERSION,
    bindings: keymap.bindings.map((b) => ({
      action: b.action,
      chords: b.chords.map((c) => ({ key: c.key, mods: c.mods })),
    })),
  };
}

/**
 * Parse an unknown stored value into a live `Keymap`, or `null` if it's
 * absent / corrupt / a version we don't understand. Never throws.
 */
export function parseKeymap(raw: unknown): Keymap | null {
  if (raw === null || raw === undefined) return null;
  const parsed = STORED_KEYMAP_SCHEMA.safeParse(raw);
  if (!parsed.success) return null;
  const data = parsed.data;
  // Unknown version → treat as absent (safe default). A migration table can
  // slot in here later.
  if (data.version !== KEYMAP_VERSION) return null;

  const bindings: KeyBinding[] = [];
  for (const b of data.bindings) {
    const action = reviveAction(b.action);
    /* c8 ignore next -- schema already bounds slot; defensive. */
    if (action === null) return null;
    const chords: KeyChord[] = b.chords.map((c) => ({
      key: c.key,
      mods: reviveMods(c.mods),
    }));
    bindings.push({ action, chords });
  }
  return { bindings };
}

/**
 * Re-brand a validated stored action into a live `GameAction`. The slot was
 * range-checked by the schema; `toAbilitySlot` re-applies the brand. Returns
 * null only if the brand constructor unexpectedly rejects (defensive).
 */
function reviveAction(stored: StoredKeymap["bindings"][number]["action"]): GameAction | null {
  switch (stored.kind) {
    case "castSlot": {
      try {
        return { kind: "castSlot", slot: toAbilitySlot(stored.slot) };
      } catch {
        /* c8 ignore next -- slot already validated in-range by the schema. */
        return null;
      }
    }
    case "targetNearestEnemy":
      return { kind: "targetNearestEnemy" };
    case "cycleEnemyTarget":
      return { kind: "cycleEnemyTarget" };
    case "cycleAllyTarget":
      return { kind: "cycleAllyTarget" };
    case "clearHostile":
      return { kind: "clearHostile" };
    case "clearFriendly":
      return { kind: "clearFriendly" };
    case "arenaTarget":
      return { kind: "arenaTarget", index: stored.index };
    case "partyTarget":
      return { kind: "partyTarget", index: stored.index };
    case "stopCasting":
      return { kind: "stopCasting" };
    case "toggleAutoAttack":
      return { kind: "toggleAutoAttack" };
    case "openChat":
      return { kind: "openChat" };
  }
}

function reviveMods(m: z.infer<typeof MODIFIER_SET_SCHEMA>): ModifierSet {
  return { ctrl: m.ctrl, alt: m.alt, shift: m.shift, meta: m.meta };
}
